import React, { useState } from "react";
import { Box, Typography, IconButton, Button } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";

// Compact banner animations
const compactBannerStyles = `
  .compact-banner-slide {
    transition: all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: scale(1);
  }
  .compact-banner-container:hover .compact-banner-slide {
    transform: scale(1.02);
  }
  .compact-banner-text {
    animation: textSlideUp 1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  @keyframes textSlideUp {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .compact-nav-button {
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  .compact-nav-button:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
`;

// Inject styles
if (
  typeof document !== "undefined" &&
  !document.querySelector("#compact-banner-animations")
) {
  const style = document.createElement("style");
  style.id = "compact-banner-animations";
  style.textContent = compactBannerStyles;
  document.head.appendChild(style);
}

export default function CompactBanner({ banners, handleCTANavigation }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Navigation functions
  const handlePrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? sortedBanners.length - 1 : prevIndex - 1
    );
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) => (prevIndex + 1) % sortedBanners.length);
  };

  // Sort banners by Banner_Position for proper slider order
  const sortedBanners = banners.sort(
    (a, b) => (a.Banner_Position || 0) - (b.Banner_Position || 0)
  );

  // Auto-rotate through banners if multiple
  React.useEffect(() => {
    if (sortedBanners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % sortedBanners.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [sortedBanners.length]);

  if (!banners || banners.length === 0) {
    return null;
  }

  const currentBanner = sortedBanners[currentIndex];

  return (
    <Box
      className="compact-banner-container"
      sx={{
        position: "relative",
        width: "100%",
        height: {
          xs: "120px",
          sm: "150px",
          md: "50vh",
          lg: "50vh",
          xl: "50vh",
        },
        mt: 12,
        mb: 4,
        overflow: "hidden",
        boxShadow: 4,
        borderRadius: "16px",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? theme.palette.grey[900]
            : theme.palette.grey[100],
        borderRadius: "8px",
        cursor: currentBanner?.Banner_Link ? "pointer" : "default",
        transition: "transform 0.3s ease",
        "&:hover": {
          transform: currentBanner?.Banner_Link ? "scale(1.02)" : "none",
        },
      }}
      onClick={() => {
        if (currentBanner?.Banner_Link) {
          handleCTANavigation(currentBanner.Banner_Link);
        }
      }}
    >
      {/* Banner Image */}
      <img
        src={process.env.BASEURL + currentBanner.Banner_Image_File_Path}
        alt={currentBanner.Image_Heading_Text || "Banner"}
        className="compact-banner-slide"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
          filter: "brightness(0.9) saturate(1.05) contrast(1.1)",
        }}
      />

      {/* Overlay for dark/light mode */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.85) 100%)"
              : "linear-gradient(180deg, rgba(255,255,255,0.15) 60%, rgba(255,255,255,0.7) 100%)",
          zIndex: 1,
        }}
      />

      {/* Text Content */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          pb: 2,
          px: 2,
        }}
      >
        {currentBanner.Image_Heading_Text && (
          <Typography
            variant="h5"
            className="compact-banner-text"
            sx={{
              color: "#fff",
              fontWeight: 700,
              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
              mb: 0.5,
              textAlign: "center",
              letterSpacing: 1,
              fontSize: { xs: "1rem", sm: "1.2rem", md: "1.3rem" },
            }}
          >
            {currentBanner.Image_Heading_Text}
          </Typography>
        )}

        {currentBanner.Image_Sub_Heading_Text && (
          <Typography
            variant="subtitle2"
            sx={{
              color: "#fff",
              fontWeight: 400,
              textShadow: "0 1px 4px rgba(0,0,0,0.3)",
              textAlign: "center",
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
              mb:
                currentBanner.Banner_CTA_Text &&
                currentBanner.Banner_CTA_Button_Link
                  ? 1
                  : 0,
            }}
          >
            {currentBanner.Image_Sub_Heading_Text}
          </Typography>
        )}

        {/* Small CTA Button */}
        {currentBanner.Banner_CTA_Text &&
          currentBanner.Banner_CTA_Button_Link && (
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCTANavigation(currentBanner.Banner_CTA_Button_Link);
              }}
              sx={{
                px: 2,
                py: 0.5,
                fontSize: "0.75rem",
                fontWeight: 600,
                borderRadius: "20px",
                textTransform: "none",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                color: (theme) => theme.palette.primary.main,
                backdropFilter: "blur(8px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                "&:hover": {
                  backgroundColor: "white",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {currentBanner.Banner_CTA_Text}
            </Button>
          )}
      </Box>

      {/* Navigation arrows for multiple banners */}
      {sortedBanners.length > 1 && (
        <>
          {/* Previous arrow */}
          <IconButton
            onClick={handlePrevious}
            className="compact-nav-button"
            sx={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 3,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(0, 0, 0, 0.7)"
                  : "rgba(255, 255, 255, 0.8)",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#fff" : "#333",
              width: 32,
              height: 32,
              backdropFilter: "blur(4px)",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(0, 0, 0, 0.85)"
                    : "rgba(255, 255, 255, 0.95)",
              },
            }}
          >
            <ArrowBackIos sx={{ fontSize: 16 }} />
          </IconButton>

          {/* Next arrow */}
          <IconButton
            onClick={handleNext}
            className="compact-nav-button"
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 3,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(0, 0, 0, 0.7)"
                  : "rgba(255, 255, 255, 0.8)",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#fff" : "#333",
              width: 32,
              height: 32,
              backdropFilter: "blur(4px)",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(0, 0, 0, 0.85)"
                    : "rgba(255, 255, 255, 0.95)",
              },
            }}
          >
            <ArrowForwardIos sx={{ fontSize: 16 }} />
          </IconButton>
        </>
      )}

      {/* Slide indicators for multiple banners */}
      {sortedBanners.length > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            right: 12,
            zIndex: 3,
            display: "flex",
            gap: 0.5,
          }}
        >
          {sortedBanners.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor:
                  index === currentIndex ? "#fff" : "rgba(255,255,255,0.5)",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
